import {
  type CSSProperties,
  type ChangeEvent,
  type FormEvent,
  useMemo,
  useRef,
  useState
} from 'react';
import { useNavigate } from 'react-router-dom';
import { AlbumFill, PlusCircle, Xmark } from '@peacecover/icon';
import GradientLoadingSpinner from '../components/GradientLoadingSpinner';
import { useGetBettieFeedPosts } from '../../domains/bettie/ui/hooks/useGetBettieFeedPosts';
import { useCreateBettieFeedPost } from '../../domains/bettie/ui/hooks/useCreateBettieFeedPost';
import type { BettieFeedPost } from '../../domains/bettie/services/bettieFeedService';
import { resizeToBase64 } from '../../utils/resizeImage';
import { daysSinceInclusive } from '../../utils/DateUtil';
import type { components } from '../../types/schema';
import './BettieFeedGallery.css';

type BettieChatRoom = components['schemas']['ChatRoom'];

interface BettieFeedGalleryProps {
  chatRoomId: string;
  chatRoom?: BettieChatRoom;
}

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const CARD_LAYOUTS = [
  { top: 78, left: 68, width: 244, height: 188, rotate: 5.4, type: 'photo' },
  { top: 246, left: 52, width: 246, height: 188, rotate: -6, type: 'photo' },
  { top: 414, left: 82, width: 244, height: 186, rotate: 5.6, type: 'photo' },
  { top: 575, left: 68, width: 222, height: 302, rotate: -7.6, type: 'strip' },
  { top: 850, left: 78, width: 240, height: 310, rotate: 12.5, type: 'strip' }
] as const;

const NOTE_LAYOUTS = [
  { top: 46, left: 6, width: 112, rotate: -7, align: 'left', arrow: '↘' },
  { top: 282, left: 258, width: 110, rotate: -7, align: 'right', arrow: '↙' },
  { top: 660, left: 258, width: 98, rotate: -7, align: 'right', arrow: '↙' }
] as const;

const BettieFeedGallery = ({ chatRoomId, chatRoom }: BettieFeedGalleryProps) => {
  const navigate = useNavigate();
  const [isComposerOpen, setIsComposerOpen] = useState(false);
  const { data, isLoading } = useGetBettieFeedPosts(chatRoomId, { page: 1, per_page: 60 });

  const photoPosts = useMemo(
    () => (data?.bettieFeedPosts ?? []).filter((post) => Boolean(post.photoUrl)),
    [data?.bettieFeedPosts]
  );
  const referenceDate = useMemo(() => getReferenceDate(photoPosts), [photoPosts]);

  const handlePostClick = (post: BettieFeedPost) => {
    navigate(`/bettie-chats/${chatRoomId}/feed/${post.bettieFeedPostId}`);
  };

  return (
    <>
      <FeedCreateButton onClick={() => setIsComposerOpen(true)} />
      <main className="relative min-h-screen overflow-hidden bg-[#f7f6f3] pt-[calc(62px+env(safe-area-inset-top))] pb-[calc(96px+env(safe-area-inset-bottom))] dark:bg-[#12110f]">
        <div className="pointer-events-none absolute left-0 top-0 h-[228px] w-full bg-[linear-gradient(180deg,#ffffff_0%,#ffffff_56%,rgba(255,255,255,0)_100%)] dark:bg-[linear-gradient(180deg,#201f1c_0%,#181715_58%,rgba(18,17,15,0)_100%)]" />
        <div className="relative mx-auto w-full max-w-[375px]">
          <DiaryTopSummary chatRoom={chatRoom} referenceDate={referenceDate} />
          {isLoading ? (
            <div className="flex justify-center pt-[86px]">
              <GradientLoadingSpinner size={32} />
            </div>
          ) : photoPosts.length === 0 ? (
            <DiaryEmptyState onCreate={() => setIsComposerOpen(true)} />
          ) : (
            <DiaryLogCanvas
              posts={photoPosts}
              referenceDate={referenceDate}
              onPostClick={handlePostClick}
            />
          )}
        </div>
      </main>
      {isComposerOpen && (
        <BettieFeedComposer chatRoomId={chatRoomId} onClose={() => setIsComposerOpen(false)} />
      )}
    </>
  );
};

export default BettieFeedGallery;

const DiaryTopSummary = ({
  chatRoom,
  referenceDate
}: {
  chatRoom?: BettieChatRoom;
  referenceDate: Date;
}) => {
  const weekDays = useMemo(() => getWeekDays(referenceDate), [referenceDate]);
  const bettieName = chatRoom?.bettieProfile?.nickname || chatRoom?.bettieNickname || 'Bettie';
  const days = chatRoom?.createdAt ? daysSinceInclusive(chatRoom.createdAt) : 1;
  const bettieImage = chatRoom?.bettieProfile?.thumbnailUrl || chatRoom?.bettieProfile?.pictureUrl;
  const myImage = chatRoom?.myProfile?.thumbnailUrl || chatRoom?.myProfile?.pictureUrl;

  return (
    <section className="relative px-[20px] pt-[18px] pb-[18px]">
      <div>
        <p className="text-[13px] font-bold leading-[16px] text-[#666666] dark:text-[#a9a39b]">
          with {bettieName}
        </p>
        <p className="mt-[2px] text-[26px] font-black leading-[29px] text-[#666666] [text-shadow:0_2px_3px_rgba(0,0,0,0.28)] dark:text-[#d5d0c8] dark:[text-shadow:0_2px_5px_rgba(0,0,0,0.45)]">
          {days} days♥
        </p>
      </div>
      <div className="absolute right-[17px] top-[18px] flex h-[62px] w-[90px] items-center justify-end">
        <DiaryAvatar src={myImage} label="me" className="translate-x-[14px]" />
        <DiaryAvatar src={bettieImage} label={bettieName} />
      </div>
      <div className="mt-[25px] grid grid-cols-7 text-center">
        {weekDays.map((day) => {
          const isActive = isSameDate(day, referenceDate);
          return (
            <div key={day.toISOString()} className="flex min-w-0 flex-col items-center gap-[5px]">
              <span
                className={`text-[12px] font-semibold leading-[14px] ${
                  isActive ? 'text-[#6e9dfc]' : 'text-[#777777] dark:text-[#85807a]'
                }`}
              >
                {DAY_LABELS[day.getDay()]}
              </span>
              <span
                className={`text-[14px] font-bold leading-[17px] ${
                  isActive ? 'text-[#6e9dfc]' : 'text-[#333333] dark:text-[#ddd8d0]'
                }`}
              >
                {day.getDate()}
              </span>
              <span
                className={`h-[4px] w-[4px] rounded-full ${
                  isActive ? 'bg-[#6e9dfc]' : 'bg-transparent'
                }`}
              />
            </div>
          );
        })}
      </div>
    </section>
  );
};

const DiaryAvatar = ({
  src,
  label,
  className = ''
}: {
  src?: string | null;
  label: string;
  className?: string;
}) => {
  return (
    <div
      className={`relative h-[60px] w-[60px] shrink-0 overflow-hidden rounded-full border-[3px] border-white bg-[#ded8cd] shadow-[0_1px_5px_rgba(0,0,0,0.14)] dark:border-[#28251f] dark:bg-[#3a342c] ${className}`}
    >
      {src ? (
        <img
          src={src}
          alt={label}
          className="absolute inset-0 h-full w-full rounded-full object-cover"
        />
      ) : (
        <div className="flex h-full w-full items-center justify-center rounded-full bg-[#d3cabb] text-[15px] font-bold text-white dark:bg-[#4a4237]">
          {label.charAt(0).toUpperCase()}
        </div>
      )}
    </div>
  );
};

const DiaryLogCanvas = ({
  posts,
  referenceDate,
  onPostClick
}: {
  posts: BettieFeedPost[];
  referenceDate: Date;
  onPostClick: (post: BettieFeedPost) => void;
}) => {
  const canvasHeight = Math.max(830, Math.ceil(posts.length / CARD_LAYOUTS.length) * 800 + 410);

  return (
    <section className="relative">
      <div className="relative z-[2] flex items-baseline justify-center gap-[20px] pt-[7px]">
        <p className="bettie-feed-handwriting -rotate-[7deg] text-[25px] leading-none text-[#222222] dark:text-[#ede8df]">
          {referenceDate.getMonth() + 1}월
        </p>
        <p className="bettie-feed-handwriting text-[25px] leading-none text-[#111111] dark:text-[#f4efe7]">
          {posts.length}개의 로그
        </p>
      </div>
      <div className="relative mx-auto mt-[12px] w-[375px]" style={{ height: canvasHeight }}>
        {NOTE_LAYOUTS.map((layout, index) => {
          const post = posts[index];
          if (!post) return null;

          return (
            <DiaryNote
              // biome-ignore lint/suspicious/noArrayIndexKey: fixed decorative note slots
              key={`note-${index}`}
              post={post}
              layout={layout}
            />
          );
        })}
        <BettieSticker />
        {posts.map((post, index) => (
          <DiaryCard
            key={post.bettieFeedPostId}
            post={post}
            index={index}
            onClick={() => onPostClick(post)}
          />
        ))}
      </div>
    </section>
  );
};

const DiaryCard = ({
  post,
  index,
  onClick
}: {
  post: BettieFeedPost;
  index: number;
  onClick: () => void;
}) => {
  const layout = getCardLayout(index);
  const postedDate = getPostDate(post);
  const isStrip = layout.type === 'strip';
  const style: CSSProperties = {
    top: layout.top,
    left: layout.left,
    width: layout.width,
    height: layout.height,
    transform: `rotate(${layout.rotate}deg)`
  };

  return (
    <button
      type="button"
      aria-label={post.body || '베티 다이어리 로그'}
      onClick={onClick}
      className="absolute z-[3] block text-left outline-none transition-transform active:scale-[0.98]"
      style={style}
    >
      {isStrip ? <PhotoStrip post={post} /> : <PhotoCard post={post} />}
      {postedDate && <DiaryDateBadge date={postedDate} />}
    </button>
  );
};

const PhotoCard = ({ post }: { post: BettieFeedPost }) => {
  return (
    <div className="h-full w-full rounded-[14px] bg-white p-[7px] shadow-[0_8px_18px_rgba(35,35,35,0.18)] dark:bg-[#f4efe5] dark:shadow-[0_10px_22px_rgba(0,0,0,0.35)]">
      <img
        src={post.photoUrl ?? ''}
        alt={post.body || post.authorName || 'bettie feed'}
        className="h-full w-full rounded-[10px] object-cover"
        loading="lazy"
      />
    </div>
  );
};

const PhotoStrip = ({ post }: { post: BettieFeedPost }) => {
  return (
    <div className="h-full w-full bg-[#b9d2ed] p-[10px] shadow-[0_8px_18px_rgba(35,35,35,0.18)] dark:bg-[#8fb0cf] dark:shadow-[0_10px_22px_rgba(0,0,0,0.35)]">
      <div className="grid h-full grid-cols-2 grid-rows-4 gap-[6px]">
        {Array.from({ length: 8 }).map((_, index) => (
          <div
            // biome-ignore lint/suspicious/noArrayIndexKey: repeated contact sheet cells are static
            key={index}
            className="overflow-hidden border-[2px] border-white bg-white"
          >
            <img
              src={post.photoUrl ?? ''}
              alt=""
              className="h-full w-full object-cover"
              loading="lazy"
            />
          </div>
        ))}
      </div>
    </div>
  );
};

const DiaryDateBadge = ({ date }: { date: Date }) => {
  return (
    <div className="bettie-feed-handwriting absolute bottom-[-22px] left-[0] min-w-[26px] text-center text-black dark:text-[#f3ede3]">
      <p className="text-[10px] font-bold leading-[10px]">{getMonthShort(date)}</p>
      <p className="text-[15px] font-bold leading-[15px]">{date.getDate()}</p>
    </div>
  );
};

const DiaryNote = ({
  post,
  layout
}: {
  post: BettieFeedPost;
  layout: (typeof NOTE_LAYOUTS)[number];
}) => {
  const authorName = post.authorName || (post.authorType === 'bettie' ? '베티' : '나');
  const content = getNoteContent(post);

  return (
    <div
      className="bettie-feed-handwriting absolute z-[4] text-[#636363] [text-shadow:0_0_0.7px_rgba(0,0,0,0.45)] dark:text-[#d9d0c4] dark:[text-shadow:0_1px_1px_rgba(0,0,0,0.75)]"
      style={{
        top: layout.top,
        left: layout.left,
        width: layout.width,
        transform: `rotate(${layout.rotate}deg)`,
        textAlign: layout.align
      }}
    >
      <p className="text-[15px] leading-[16px]">{authorName}</p>
      <p className="mt-[4px] whitespace-pre-line text-[15px] leading-[17px]">{content}</p>
      <p className="mt-[5px] text-[25px] leading-none text-[#8b8b8b] dark:text-[#b2aa9f]">
        {layout.arrow}
      </p>
    </div>
  );
};

const BettieSticker = () => {
  return (
    <div className="absolute left-[284px] top-[126px] z-[5] flex h-[62px] w-[64px] -rotate-[12deg] items-center justify-center rounded-[45%] border-[2px] border-[#4d4d4d] bg-[#ffb7c8] shadow-[0_4px_8px_rgba(0,0,0,0.08)] dark:border-[#2a2424] dark:bg-[#f09caf]">
      <div className="relative h-[30px] w-[34px]">
        <span className="absolute left-[6px] top-[8px] h-[3px] w-[3px] rounded-full bg-[#4d4d4d]" />
        <span className="absolute right-[6px] top-[8px] h-[3px] w-[3px] rounded-full bg-[#4d4d4d]" />
        <span className="absolute left-[12px] top-[18px] h-[5px] w-[10px] rounded-b-full border-b border-[#4d4d4d]" />
      </div>
    </div>
  );
};

const DiaryEmptyState = ({ onCreate }: { onCreate: () => void }) => {
  return (
    <section className="relative mx-auto mt-[12px] h-[640px] w-[375px]">
      <div className="relative z-[2] flex items-baseline justify-center gap-[20px] pt-[7px]">
        <p className="bettie-feed-handwriting -rotate-[7deg] text-[25px] leading-none text-[#222222] dark:text-[#ede8df]">
          {new Date().getMonth() + 1}월
        </p>
        <p className="bettie-feed-handwriting text-[25px] leading-none text-[#111111] dark:text-[#f4efe7]">
          0개의 로그
        </p>
      </div>
      <button
        type="button"
        onClick={onCreate}
        className="absolute left-[68px] top-[104px] h-[188px] w-[244px] rotate-[5.4deg] rounded-[14px] bg-white p-[7px] text-left shadow-[0_8px_18px_rgba(35,35,35,0.16)] dark:bg-[#f4efe5] dark:shadow-[0_10px_22px_rgba(0,0,0,0.35)]"
      >
        <div className="flex h-full w-full flex-col items-center justify-center rounded-[10px] bg-[#ede9de] dark:bg-[#dbd3c7]">
          <AlbumFill size={32} className="text-[#9b9182]" />
          <p className="mt-[8px] text-[13px] font-bold leading-[16px] text-[#6f675d]">
            첫 로그 남기기
          </p>
        </div>
      </button>
      <div className="bettie-feed-handwriting absolute left-[12px] top-[76px] z-[4] w-[116px] -rotate-[7deg] text-[#636363] [text-shadow:0_0_0.7px_rgba(0,0,0,0.45)] dark:text-[#d9d0c4]">
        <p className="text-[15px] leading-[17px]">사진과 글로</p>
        <p className="text-[15px] leading-[17px]">오늘의 순간을 남겨봐!</p>
        <p className="mt-[5px] text-[25px] leading-none text-[#8b8b8b] dark:text-[#b2aa9f]">↘</p>
      </div>
      <BettieSticker />
    </section>
  );
};

const FeedCreateButton = ({ onClick }: { onClick: () => void }) => {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="피드 작성"
      className="fixed right-[calc(16px+env(safe-area-inset-right))] bottom-[calc(20px+env(safe-area-inset-bottom))] z-[850] flex h-[52px] w-[52px] items-center justify-center rounded-full bg-[#222222] text-white shadow-[0_6px_18px_rgba(0,0,0,0.18)] dark:bg-[#f4efe7] dark:text-[#222222]"
    >
      <PlusCircle size={30} className="text-white dark:text-[#222222]" />
    </button>
  );
};

const BettieFeedComposer = ({
  chatRoomId,
  onClose
}: {
  chatRoomId: string;
  onClose: () => void;
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [body, setBody] = useState('');
  const [dataUrl, setDataUrl] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isReadingImage, setIsReadingImage] = useState(false);
  const createFeedPost = useCreateBettieFeedPost();

  const handleSelectImage = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setErrorMessage('이미지 파일만 선택할 수 있어요.');
      return;
    }

    setErrorMessage('');
    setIsReadingImage(true);
    try {
      const resizedDataUrl = await resizeToBase64(file);
      setDataUrl(resizedDataUrl);
    } catch (_error) {
      setErrorMessage('이미지를 불러오지 못했어요. 다시 시도해 주세요.');
    } finally {
      setIsReadingImage(false);
    }
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!dataUrl || createFeedPost.isLoading || isReadingImage) return;

    setErrorMessage('');
    try {
      await createFeedPost.mutateAsync({
        chatRoomId,
        body: {
          bettie_feed_post: {
            body: body.trim() || null,
            data_url: dataUrl
          }
        }
      });
      setBody('');
      setDataUrl('');
      onClose();
    } catch (_error) {
      setErrorMessage('피드를 올리지 못했어요. 다시 시도해 주세요.');
    }
  };

  const isSubmitting = createFeedPost.isLoading || isReadingImage;

  return (
    <div className="fixed inset-0 z-[1200] flex items-end justify-center bg-black/45">
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-prose rounded-t-[20px] bg-background-light px-[16px] pt-[16px] pb-[calc(18px+env(safe-area-inset-bottom))] shadow-[0_-10px_32px_rgba(0,0,0,0.18)] dark:bg-background-dark"
      >
        <div className="flex items-center justify-between">
          <h2 className="text-[17px] font-bold leading-[20px] text-text-main-lv1">피드 작성</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="닫기"
            className="flex h-[32px] w-[32px] items-center justify-center"
          >
            <Xmark size={22} className="text-text-main-lv1" />
          </button>
        </div>

        <button
          type="button"
          onClick={handleSelectImage}
          disabled={isSubmitting}
          className="mt-[16px] flex aspect-square w-full items-center justify-center overflow-hidden rounded-[8px] bg-surface-lv2"
        >
          {dataUrl ? (
            <img src={dataUrl} alt="" className="h-full w-full object-cover" />
          ) : (
            <div className="flex flex-col items-center gap-[8px]">
              <AlbumFill size={32} className="text-text-main-lv3" />
              <p className="text-[14px] font-semibold leading-[17px] text-text-main-lv3">
                사진 선택
              </p>
            </div>
          )}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleFileChange}
          disabled={isSubmitting}
        />

        <textarea
          value={body}
          onChange={(event) => setBody(event.target.value)}
          placeholder="글을 입력하세요"
          maxLength={1000}
          rows={4}
          className="mt-[12px] w-full resize-none rounded-[8px] bg-surface-lv2 px-[14px] py-[12px] text-[14px] leading-[20px] text-text-main-lv1 outline-none placeholder:text-text-main-lv4"
          disabled={isSubmitting}
        />

        {errorMessage && (
          <p className="mt-[8px] text-[12px] leading-[16px] text-warning dark:text-[#FF495A]">
            {errorMessage}
          </p>
        )}

        <button
          type="submit"
          disabled={!dataUrl || isSubmitting}
          className="mt-[14px] h-[48px] w-full rounded-[8px] bg-primary-light text-[15px] font-semibold leading-none text-white disabled:bg-surface-lv3 disabled:text-text-main-lv4 dark:bg-primary-dark"
        >
          {isSubmitting ? '올리는 중' : '올리기'}
        </button>
      </form>
    </div>
  );
};

function getCardLayout(index: number) {
  const layout = CARD_LAYOUTS[index % CARD_LAYOUTS.length];
  const block = Math.floor(index / CARD_LAYOUTS.length);
  return {
    ...layout,
    top: layout.top + block * 800
  };
}

function getReferenceDate(posts: BettieFeedPost[]) {
  const date = getPostDate(posts[0]);
  return date ?? new Date();
}

function getWeekDays(referenceDate: Date) {
  const firstDay = new Date(referenceDate);
  firstDay.setHours(0, 0, 0, 0);
  firstDay.setDate(referenceDate.getDate() - ((referenceDate.getDay() + 6) % 7));

  return Array.from({ length: 7 }, (_, index) => {
    const date = new Date(firstDay);
    date.setDate(firstDay.getDate() + index);
    return date;
  });
}

function isSameDate(a: Date, b: Date) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function getPostDate(post?: BettieFeedPost) {
  if (!post) return null;

  const record = post as BettieFeedPost & {
    createdAt?: unknown;
    created_at?: unknown;
    posted_at?: unknown;
    uploadedAt?: unknown;
    uploaded_at?: unknown;
  };

  return (
    parseDate(record.postedAt) ??
    parseDate(record.posted_at) ??
    parseDate(record.uploadedAt) ??
    parseDate(record.uploaded_at) ??
    parseDate(record.createdAt) ??
    parseDate(record.created_at)
  );
}

function parseDate(value: unknown): Date | null {
  if (typeof value === 'string' || typeof value === 'number') {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date;
  }

  if (!value || typeof value !== 'object') return null;

  const record = value as Record<string, unknown>;
  const candidate =
    record.iso8601 ??
    record.iso ??
    record.value ??
    record.date ??
    record.datetime ??
    record.time ??
    record.timestamp;

  if (typeof candidate !== 'string' && typeof candidate !== 'number') return null;

  const date = new Date(candidate);
  if (Number.isNaN(date.getTime())) return null;
  return date;
}

function getMonthShort(date: Date) {
  return date.toLocaleString('en-US', { month: 'short' }).toUpperCase();
}

function getNoteContent(post: BettieFeedPost) {
  const content = post.body?.trim();
  if (!content) return '같이 남기고 싶은\n오늘의 기록';

  const normalized = content.replace(/\s+/g, ' ');
  if (normalized.length <= 24) return normalized;
  return `${normalized.slice(0, 24)}...`;
}
